/**
 * Insights hub card for Roster & MIPS Exposure (owner 2026-07-15): the card
 * itself carries a live summary — roster counts, the APPLIED quality composite,
 * and the PI-reweighted MIPS exposure — mirroring the MIPS Insights card
 * pattern (summary on the face, click to open the report).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReportCard } from '@/components/insights/ReportCard';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { logicForYear } from '@/lib/app-plus/years/registry';
import { useAcoRosterDerived } from '@/hooks/queries/useMipsQueries';
import { useAppPlusScoring } from '../lib/useAppPlusScoring';

export function RosterExposureCard() {
  const navigate = useNavigate();
  const { selectedAco } = useAco();
  const { programYear } = useProgramYear();
  const scoring = useAppPlusScoring(selectedAco?.id ?? null, selectedAco?.shadow_practice_id ?? null);
  const derivedQ = useAcoRosterDerived(selectedAco?.id ?? null, programYear);
  const derived = derivedQ.data ?? null;
  // Cold load only — cached/held data paints counts immediately (no
  // "No roster imported…" → real counts swap that shifted this footer line).
  const rosterPending = derivedQ.isPending && !derivedQ.data;

  const logic = useMemo(() => {
    try { return logicForYear(programYear); } catch { return null; }
  }, [programYear]);

  // Applied score → PI-reweighted exposure branch (the no-PI-submission default;
  // the report's score strip lets the user project a PI score).
  const quality = scoring.applied?.mipsQualityInput ?? null;
  const exposure = logic && quality != null
    ? logic.computeMipsExposure({ qualityPct: quality, piPct: null }, logic.getConstants(programYear))
    : null;
  const branch = exposure?.reweighted ?? null;

  // No intermediate numbers while the hook loads (flash fix, owner 2026-07-15).
  const preview = scoring.loading ? (
    <div className="w-full py-6 text-center text-sm text-muted-foreground">Loading…</div>
  ) : (
    <div className="w-full py-2">
      <div className="flex items-center justify-around">
        <div className="text-center">
          <p className="text-4xl font-bold tabular-nums">{quality != null ? quality.toFixed(1) : '—'}</p>
          <p className="text-xs text-muted-foreground mt-1">Applied Quality Composite</p>
        </div>
        <div className="h-16 w-px bg-border" />
        <div className="text-center">
          {branch ? (
            <>
              <div className="flex items-center justify-center gap-2">
                {branch.positiveScaled
                  ? <TrendingUp className="h-5 w-5 text-emerald-500" />
                  : (branch.adjustmentPct ?? 0) < 0 && <TrendingDown className="h-5 w-5 text-red-500" />}
                <p className={cn('text-3xl font-bold tabular-nums',
                  branch.positiveScaled ? 'text-emerald-600'
                    : (branch.adjustmentPct ?? 0) < 0 ? 'text-red-600' : 'text-slate-600')}>
                  {branch.positiveScaled ? '+%' : `${branch.adjustmentPct}%`}
                </p>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                MIPS {branch.finalScore.toFixed(1)} · PI reweighted
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-semibold text-muted-foreground">—</p>
              <p className="text-xs text-muted-foreground mt-1">Score on QPS Management to see</p>
            </>
          )}
        </div>
      </div>
      <p className="mt-3 border-t pt-2 text-center text-xs text-muted-foreground min-h-[1rem]">
        {rosterPending ? (
          <span className="inline-block h-3 w-56 animate-pulse rounded bg-muted align-middle" aria-hidden />
        ) : derived?.imported ? (
          <>{derived.practices} practices · {derived.clinicians} clinicians · {derived.mustReport.required} must-report · {derived.qps} QPs</>
        ) : (
          'No roster imported for this year — upload the ACO Eligibility File'
        )}
      </p>
    </div>
  );

  return (
    <ReportCard
      title="Roster & MIPS Exposure"
      description="Participant practices grouped at the entity level, must-report coverage, and the §7.4 MIPS exposure the applied APP score creates."
      icon={<Users className="h-6 w-6" />}
      onClick={() => navigate('/programs/app-plus/insights/roster-exposure')}
      status="available"
      preview={preview}
    />
  );
}
